import React from "react";
import { useTranslation } from "react-i18next";
import { Activity } from "lucide-react";
import HealthRadarChart from "./HealthRadarChart";

/**
 * QENVA · Radar Santé Entreprise™ enriched.
 *
 * Hexagonal radar (existing HealthRadarChart) plus a 6-card grid below
 * with score + colored bar + label for each axis (Juridique, Assurance,
 * Financement, RH, Contrats, Conformité) — per the Figma.
 */
function scoreColor(score) {
  if (score >= 90) return { bar: "bg-emerald-500", text: "text-emerald-600" };
  if (score >= 75) return { bar: "bg-qenva-blue", text: "text-qenva-blue" };
  if (score >= 60) return { bar: "bg-amber-500", text: "text-amber-600" };
  return { bar: "bg-rose-500", text: "text-rose-600" };
}

export default function HealthRadarExpanded({ healthRadar, testId = "health-radar-expanded" }) {
  const { t } = useTranslation("dashboard");
  const axes = healthRadar?.axes || [];
  // Build an axis-by-key lookup; fall back to mock if not present
  const byKey = {};
  for (const a of axes) byKey[a.axis] = a;

  // Order per Figma: Juridique, Assurance, Financement, RH, Contrats, Conformité
  const display = ["legal", "insurance", "financing", "hr", "contracts", "compliance"];
  const labels = {
    legal: t("health_radar_expanded.axis_legal"),
    insurance: t("health_radar_expanded.axis_insurance"),
    financing: t("health_radar_expanded.axis_financing"),
    hr: t("health_radar_expanded.axis_hr"),
    contracts: t("health_radar_expanded.axis_contracts"),
    compliance: t("health_radar_expanded.axis_compliance"),
  };

  return (
    <section className="rounded-2xl border border-zinc-200 bg-white p-5 min-w-0 overflow-hidden" data-testid={testId}>
      <header className="flex items-center gap-2 mb-3">
        <Activity size={15} className="text-qenva-blue" />
        <h2 className="font-display font-bold text-qenva-navy text-[15px] tracking-tight">
          {t("health_radar_expanded.title")}
        </h2>
      </header>

      {/* Radar chart — compact default (260px). Drives Dashboard's vertical
          rhythm; a dedicated detailed view can re-render the same component
          at its native 380px size when context allows. */}
      <div className="mb-4">
        <HealthRadarChart axes={axes} size={260} testId={`${testId}-chart`} />
      </div>

      {/* 6 mini cards — tighter padding so the radar section no longer
          dominates the dashboard vertical scroll. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid={`${testId}-axis-grid`}>
        {display.map((key) => {
          const axis = byKey[key] || {};
          const score = axis.score ?? 0;
          const color = scoreColor(score);
          const measured = !!axis.measured;
          return (
            <div
              key={key}
              className="rounded-lg bg-zinc-50 border border-zinc-200/60 px-2.5 py-2"
              data-testid={`${testId}-axis-${key}`}
            >
              <p className="text-[9.5px] uppercase tracking-[0.14em] font-bold text-zinc-500">
                {labels[key]}
              </p>
              <p className={`font-display font-bold text-[18px] tabular-nums leading-none mt-0.5 ${measured ? color.text : "text-zinc-300"}`}>
                {measured ? score : "—"}
              </p>
              <div className="h-[3px] rounded-full bg-zinc-200 mt-1.5 overflow-hidden">
                {measured && (
                  <div className={`h-full rounded-full ${color.bar}`} style={{ width: `${Math.max(2, score)}%` }} />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
